'use client'
import { type ComponentPropsWithoutRef, type ReactNode, useId, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { Portal } from '@radix-ui/react-portal'
import { storyblokEditable } from '@storyblok/react'
import { type StoryblokRichTextNode } from '@storyblok/richtext'
import { Collapsible } from 'ui/src/components/Collapsible/Collapsible'
import { Text } from 'ui/src/components/Text/Text'
import { CampaignIcon } from 'ui/src/icons/CampaignIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { WarningTriangleIcon } from 'ui/src/icons/WarningTriangleIcon'
import { tokens } from 'ui/src/theme/tokens.css'
import { framerTransitions } from 'ui/src/theme/transitions'
import { renderRichTextToJsx } from '../RichTextBlock/richTextReactRenderer'
import { banner, content, details, textButton } from './FloatBannerBlock.css'

type Variant = 'info' | 'campaign' | 'warning' | 'error'

type FloatBannerBlockProps = SbBaseBlockProps<{
  summary: string
  details: StoryblokRichTextNode
  variant: Variant
}>

export function FloatBannerBlock({ blok }: FloatBannerBlockProps) {
  const { t } = useTranslation('common')
  const id = useId()

  const [isExpanded, setIsExpanded] = useState(false)

  const detailsContent = useMemo(() => renderRichTextToJsx(blok.details), [blok.details])

  // Gotcha: Even setting a default value for the variant, Storyblok can return '' (empty string)
  // sometimes
  const variant = blok.variant || 'info'
  const role = ['info', 'campaign'].includes(variant) ? 'status' : 'alert'

  // Render in a portal to avoid positioning issues in case fixed positioned falls into a exception
  // which would make it positioned according to the parent element instead of the viewport. E.g:
  // in case parent element include 'transform' like ModalBlock does.
  return (
    <Portal id={`float-banner-container-${id}`}>
      <Collapsible.Root onOpenChange={setIsExpanded} asChild>
        <motion.div
          className={banner({ variant })}
          role={role}
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: framerTransitions.defaultDuration,
            ...framerTransitions.easeInOutCubic,
          }}
          {...storyblokEditable(blok)}
        >
          <Icon variant={variant} />

          <Text size="xs" as="p">
            {blok.summary}
          </Text>

          <Collapsible.Trigger className={textButton}>
            <Text size="xs" as="span">
              {isExpanded ? t('FLOAT_BANNER_CLOSE') : t('FLOAT_BANNER_EXPAND')}
            </Text>
          </Collapsible.Trigger>

          <Collapsible.Content className={content}>
            <Text as="div" className={details} size="xs" balance={false}>
              {detailsContent}
            </Text>
          </Collapsible.Content>
        </motion.div>
      </Collapsible.Root>
    </Portal>
  )
}

type IconProps = ComponentPropsWithoutRef<typeof InfoIcon | typeof CampaignIcon | typeof WarningTriangleIcon> & {
  variant: Variant
}

function Icon({ variant, ...others }: IconProps) {
  const size = '1rem'

  let icon: ReactNode = null
  switch (variant) {
    case 'info':
      icon = <InfoIcon color={tokens.legacyColors.signalBlueElement} size={size} {...others} />
      break
    case 'campaign':
      icon = <CampaignIcon color={tokens.legacyColors.signalGreenElement} size={size} {...others} />
      break
    case 'warning':
      icon = <WarningTriangleIcon color={tokens.legacyColors.signalAmberElement} size={size} {...others} />
      break
    case 'error':
      icon = <WarningTriangleIcon color={tokens.legacyColors.signalRedElement} size={size} {...others} />
      break
  }

  return icon
}
